/**
 * Plugin Modal Host — Wave 22.13
 *
 * React component that renders the active modal stack. Mount this
 * near the root of the application so plugin-initiated modals appear
 * above all other content.
 *
 * The host maps `viewId` to modal components via a registry and
 * passes the stored props + a close callback to each one.
 */

import { useCallback, useEffect } from 'react';
import { useModalStackStore } from '../plugins/modal-stack-manager';
import type { ModalResult, ActiveModal } from '../types/plugin-interaction';
import { setPluginModalHostMounted } from '../plugins/interaction-warnings';

// ────────────────────────────────────────────────────────
// Modal Component Registry
// ────────────────────────────────────────────────────────

type ModalComponentType = React.ComponentType<{
  onClose: (result: ModalResult) => void;
  [key: string]: any;
}>;

const _modalComponentRegistry = new Map<string, ModalComponentType>();

/**
 * Register a modal view component that can be opened via `ui.openModal()`.
 *
 * @param viewId - The view identifier (matches `ModalRequest.viewId`).
 * @param component - React component with an `onClose` prop.
 */
export function registerModalComponent(viewId: string, component: ModalComponentType): void {
  _modalComponentRegistry.set(viewId, component);
}

/**
 * Unregister a modal view component.
 */
export function unregisterModalComponent(viewId: string): void {
  _modalComponentRegistry.delete(viewId);
}

/**
 * Get all registered modal view IDs (for DevTools).
 */
export function getRegisteredModalViewIds(): string[] {
  return Array.from(_modalComponentRegistry.keys());
}

// ────────────────────────────────────────────────────────
// Host Component
// ────────────────────────────────────────────────────────

/**
 * Renders the modal stack. Each modal is looked up in the component
 * registry by `viewId` and rendered with its `props` + `onClose`.
 *
 * Mount once at the application root:
 * ```tsx
 * <App>
 *   ...
 *   <PluginModalHost />
 * </App>
 * ```
 */
export function PluginModalHost() {
  const stack = useModalStackStore((s) => s.stack);
  const closeModal = useModalStackStore((s) => s.closeModal);

  useEffect(() => {
    setPluginModalHostMounted(true);
    return () => setPluginModalHostMounted(false);
  }, []);

  if (stack.length === 0) return null;

  return (
    <>
      {stack.map((modal) => (
        <ModalInstance key={modal.id} modal={modal} onClose={closeModal} />
      ))}
    </>
  );
}

function ModalInstance({
  modal,
  onClose,
}: {
  modal: ActiveModal;
  onClose: (result: ModalResult, modalId?: string) => void;
}) {
  const Component = _modalComponentRegistry.get(modal.viewId);

  const handleClose = useCallback(
    (result: ModalResult) => {
      onClose(result, modal.id);
    },
    [onClose, modal.id],
  );

  if (!Component) {
    console.warn(
      `[PluginModalHost] No component registered for viewId="${modal.viewId}" (plugin: ${modal.pluginId})`,
    );
    return <MissingModalWarning modal={modal} onClose={handleClose} />;
  }

  return <Component {...modal.props} onClose={handleClose} />;
}

/**
 * Fallback UI rendered when a modal's viewId has no registered component.
 * Provides diagnostic information and a dismiss button so the user isn't stuck.
 */
function MissingModalWarning({
  modal,
  onClose,
}: {
  modal: ActiveModal;
  onClose: (result: ModalResult) => void;
}) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Missing modal component"
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1100,
        padding: 24,
      }}
      data-testid="missing-modal-warning"
    >
      <div
        style={{
          background: 'var(--nc-color-surface, #1a1a2e)',
          borderRadius: 12,
          padding: 24,
          maxWidth: 480,
          width: '100%',
          boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
          border: '2px solid var(--nc-color-danger, #dc2626)',
        }}
      >
        <h3 style={{ margin: '0 0 8px', color: 'var(--nc-color-danger, #dc2626)' }}>
          ⚠ Missing Modal Component
        </h3>
        <p style={{ margin: '0 0 12px', color: 'var(--nc-color-text, #fff)', fontSize: 14 }}>
          Plugin <strong>{modal.pluginId}</strong> requested modal view{' '}
          <code style={{ background: 'rgba(255,255,255,0.1)', padding: '1px 4px', borderRadius: 3 }}>
            {modal.viewId}
          </code>{' '}
          but no component is registered for this viewId.
        </p>
        <p style={{ margin: '0 0 16px', color: 'var(--nc-color-text-muted, #888)', fontSize: 12 }}>
          Fix: Call <code>registerModalComponent('{modal.viewId}', YourComponent)</code> during
          plugin setup, or add it to <code>modalComponents</code> in your integration points.
        </p>
        <button
          onClick={() => onClose({ confirmed: false })}
          style={{
            padding: '6px 16px',
            borderRadius: 6,
            border: '1px solid var(--nc-color-border, #333)',
            background: 'var(--nc-color-danger, #dc2626)',
            color: '#fff',
            cursor: 'pointer',
            fontSize: 13,
          }}
        >
          Dismiss
        </button>
      </div>
    </div>
  );
}

export default PluginModalHost;
